import React, { useEffect, useRef, useState } from 'react';
import { EInputTypes, InputTextCustom, Combobox } from 'medere-ui-framework';
import { Big } from 'big.js';
import { Button } from 'primereact/button';
import { EarningWithholdingConceptDTO, TaxTypeDTO, WithholdingRequestDTO } from '../../interface/interface';
import { usePaymentOrder } from '../../components/context/PaymentOrderContext';
import { showMessage } from '../../utils/messages';
import { Toast, ToastMessage } from 'primereact/toast';
import { useWithholdingService } from '../../servicesHooks/Withholding/useWithholdingService';

type PaidTax = {
  taxtype: number;
  amount: Big;
  percentage: Big;
};
interface TaxTypeItemProps {
  tax: TaxTypeDTO;
  totalToPay: Big;
  setTotalToPay: (taxAmount: Big) => void;
  earningWithHoldingConcept: EarningWithholdingConceptDTO,
  setEarningWithHoldingConcept: (earningWithHoldingConcept: EarningWithholdingConceptDTO) => void
  paidTaxes: PaidTax[]; // Nueva propiedad
  addPaidTax: (taxtype: number, amount: Big, percentage: Big) => void; // Función para agregar
  removePaidTax: (taxtype: number) => void; // Función para eliminar
  supplier?: any;
  totalWithoutTaxes:Big;
}

const TaxTypeItem: React.FC<TaxTypeItemProps> = (props:TaxTypeItemProps) => {
  const { calculateWithholdings, getEarningWithholdingConceptById, getEarningWithholdingConcepts } = useWithholdingService();
  const [isLoading, setIsLoading] = useState(false);
  const [addTaxAmount, setAddTaxAmount] = useState<Big>(Big(0));
  const [addTaxButtonVisible, setAddTaxButtonVisible] = useState<boolean>(true);
  const [deleteTaxButtonVisible, setDeleteTaxButtonVisible] = useState<boolean>(false);
  const toast = useRef(null)
  const showMessage = (msg: any, ref: React.RefObject<Toast>, severity: ToastMessage['severity']) => {
    ref.current?.show({ summary: msg, life: 3000, severity:severity });
  };
  // Verificar si el impuesto ya está pagado al montar
  useEffect(() => {
    const paidTax = props.paidTaxes.find((paid) => paid.taxtype === props.tax.taxtype);
    if (paidTax) {
      setAddTaxAmount(paidTax.amount); // Establecer el monto en el input
      setAddTaxButtonVisible(false); // Deshabilitar "Calcular"
      setDeleteTaxButtonVisible(true); // Habilitar "Eliminar"
    }
  }, [props.paidTaxes, props.tax.taxname]);

  useEffect(() => {
      if (props?.supplier)
          getEarningWithholdingConceptById(props.supplier.earningwithholdingconcept)
          .then((item: EarningWithholdingConceptDTO) => {
              if (item) {
                props.setEarningWithHoldingConcept(item);
              }
          }).catch((error) => {
              console.error('Error al cargar earningWithHoldingConcept:', error);
          });
  }, [props?.supplier]);

  async function calculateTax() {
    try {
      const withHoldingResponse = await calculateWithholdings(
        props.supplier.medereentity,
        props.tax.taxtype,
        props.totalWithoutTaxes,
        props.tax.taxpercentage,
        props.earningWithHoldingConcept?.withholdingconcept
      );
      var taxAmount = withHoldingResponse.withholdingValue;
      return taxAmount
    } catch (error) {
      console.error("Error al calcular retención", error);
    }
  };

  const handleAddTax = async () => {
    try {

      if (props.tax.isearningswithholding && (!props.earningWithHoldingConcept || props.earningWithHoldingConcept.withholdingconcept === undefined || !props.earningWithHoldingConcept.withholdingconcept || props.earningWithHoldingConcept.withholdingconcept === 0)){
      showMessage('Debe seleccionar un concepto facturable para calcular retención de ganancias', toast, 'error');
      return
      }
      const taxAmount = await calculateTax();
      if (!taxAmount) {
        showMessage('No se pudo calcular el impuesto', toast, 'error');
        return;
      }    
      setAddTaxAmount(taxAmount);
      props.addPaidTax(props.tax.taxtype, taxAmount, props.tax.taxpercentage); // Agrega el impuesto a la lista pagada
      setAddTaxButtonVisible(false); // Deshabilitar "Calcular"
      setDeleteTaxButtonVisible(true); // Habilitar "Eliminar"
      } catch {
        showMessage('Error al calcular la retención', toast, 'error');
    }
  };

  const handleRemoveTax = () => {
    const taxAmount = addTaxAmount;
    setAddTaxAmount(Big(0));
    props.removePaidTax(props.tax.taxtype); // Elimina el impuesto de la lista pagada
    setAddTaxButtonVisible(true); // Habilitar "Calcular"
    setDeleteTaxButtonVisible(false); // Deshabilitar "Eliminar"
  };

  return (
    <>
      <section className="flex gap-3 align-items-center max-w-full">
        <p className="flex-shrink-0 w-32">{props.tax.taxname}</p>
        <InputTextCustom
          className="border2 mb-1 w-32 flex-shrink-0"
          inputType={EInputTypes.NUMBER}
          value={Number(addTaxAmount.toFixed(2))}
          disable
          onChange={() => {}} // Input es de solo lectura
        />
        {props.tax.isearningswithholding ? 
          <Combobox
              className="flex-grow max-w-768"
              getItems={getEarningWithholdingConcepts}
              optionLabel="description"
              placeholder="Concepto"
              value={props.earningWithHoldingConcept}
              setValue={props.setEarningWithHoldingConcept}
          /> : null
        }
        <Button
          className="buttonMain"
          style={{minWidth: 'fit-content'}}
          label="Calcular"
          type="button"
          loading={isLoading}
          onClick={handleAddTax}
          disabled={!addTaxButtonVisible} // Deshabilitado si ya está pagado
        />
        <Button
          className="buttonMain"
          style={{minWidth: 'fit-content'}}
          label="Eliminar"
          type="button"
          loading={isLoading}
          onClick={handleRemoveTax}
          disabled={!deleteTaxButtonVisible} // Deshabilitado si no está pagado
        />
      </section>
      <Toast ref={toast} position='center' style={{backgroundColor: 'white'}}/>
    </>
  );
};

export default TaxTypeItem;
